/**
 * @fileoverview Action Type Mapping for WebSocket Event Routing
 *
 * **Purpose**: Maps WebSocket event types to store-specific Redux action types.
 *
 * **Architecture**:
 * - Single source of truth for event → action mappings
 * - Type-safe with TypeScript const assertions
 * - Easy to extend for new stores (just add to the mapping)
 *
 * **Why Centralized**:
 * - Adding new store = update one file
 * - TypeScript validates all action types exist
 * - Clear overview of all routing mappings
 *
 * **Usage**:
 * ```typescript
 * const targetStore = getStoreType(chatId)  // 'playground' | 'embed' | 'inbox'
 * const actionType = ACTION_MAP.streaming[targetStore]
 * dispatch({ type: actionType, payload: { chatId, messageId, token } })
 * ```
 *
 * @module websocket/actionMapping
 * @see ./index.ts — the router architecture overview this module is part of
 */

import type { StoreType } from "./chatRegistry"

/**
 * WebSocket event types we route
 */
export type WsEventType =
  | "streaming"
  | "completed"
  | "failed"
  | "blockStart"
  | "blockUpdate"
  | "blockComplete"
  | "codeInterpreterComplete"
  | "codeInterpreterError"

/**
 * Action type mapping: Event × Store → Redux Action Type
 *
 * **Structure**:
 * ```
 * ACTION_MAP[eventType][storeType] = 'store/actionName'
 * ```
 *
 * **Example**:
 * ```typescript
 * ACTION_MAP.streaming.playground  // 'playgroundChat/addStreamingToken'
 * ACTION_MAP.streaming.embed       // 'embedChat/addEmbedStreamingToken'
 * ACTION_MAP.completed.playground  // 'playgroundChat/completeStreamingMessage'
 * ```
 *
 * **Type Safety**: TypeScript ensures all action type strings are correct
 */
export const ACTION_MAP = {
  /**
   * Streaming events - tokens arriving character-by-character
   * Fired 100+ times per second during AI response generation
   */
  streaming: {
    playground: "playgroundChat/addStreamingToken",
    embed: "embedChat/addEmbedStreamingToken",
    inbox: "inboxChat/addInboxStreamingToken" // Future
  },

  /**
   * Completion events - final message from backend
   * Fired once when AI response is complete
   */
  completed: {
    playground: "playgroundChat/completeStreamingMessage",
    embed: "embedChat/completeEmbedStreamingMessage",
    inbox: "inboxChat/completeInboxStreamingMessage" // Future
  },

  /**
   * Error events - streaming failed
   * Fired when AI generation encounters an error
   */
  failed: {
    playground: "playgroundChat/streamingError",
    embed: "embedChat/embedStreamingError",
    inbox: "inboxChat/inboxStreamingError" // Future
  },

  /**
   * Code interpreter block start - new block (thinking, tool_call, text) begins
   */
  blockStart: {
    playground: "playgroundChat/addStreamingBlock",
    embed: "embedChat/addEmbedStreamingBlock",
    inbox: "inboxChat/addInboxStreamingBlock" // Future
  },

  /**
   * Code interpreter block update - streaming content for a block
   */
  blockUpdate: {
    playground: "playgroundChat/updateStreamingBlock",
    embed: "embedChat/updateEmbedStreamingBlock",
    inbox: "inboxChat/updateInboxStreamingBlock" // Future
  },

  /**
   * Code interpreter block complete - block finished (tool call result)
   */
  blockComplete: {
    playground: "playgroundChat/completeStreamingBlock",
    embed: "embedChat/completeEmbedStreamingBlock",
    inbox: "inboxChat/completeInboxStreamingBlock" // Future
  },

  /**
   * Code interpreter execution complete - all blocks done
   */
  codeInterpreterComplete: {
    playground: "playgroundChat/completeCodeInterpreterStreaming",
    embed: "embedChat/completeEmbedCodeInterpreterStreaming",
    inbox: "inboxChat/completeInboxCodeInterpreterStreaming" // Future
  },

  /**
   * Code interpreter execution error
   */
  codeInterpreterError: {
    playground: "playgroundChat/streamingError",
    embed: "embedChat/embedStreamingError",
    inbox: "inboxChat/inboxStreamingError" // Future
  }
} as const

/**
 * Get the Redux action type for a given event and store
 *
 * **Performance**: O(1) object property access
 *
 * @param eventType - WebSocket event type (streaming/completed/failed)
 * @param storeType - Target store (playground/embed/inbox)
 * @returns Redux action type string
 *
 * @example
 * ```typescript
 * const actionType = getActionType('streaming', 'playground')
 * // Returns: 'playgroundChat/addStreamingToken'
 * ```
 */
export function getActionType(eventType: WsEventType, storeType: StoreType): string {
  const mapping = ACTION_MAP[eventType]

  if (!mapping) {
    throw new Error(`[ActionMapping] Unknown event type: ${eventType}`)
  }

  const actionType = mapping[storeType]

  if (!actionType) {
    throw new Error(
      `[ActionMapping] No action type for event "${eventType}" and store "${storeType}"`
    )
  }

  return actionType
}

/**
 * Type guard to check if a string is a valid event type
 *
 * @param value - String to check
 * @returns boolean - true if valid event type
 */
const WS_EVENT_TYPES = new Set(Object.keys(ACTION_MAP))

export function isValidEventType(value: string): value is WsEventType {
  return WS_EVENT_TYPES.has(value)
}
